import { useState } from "react";
import { Facebook, Music2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Explicit Facebook + TikTok share buttons.
 *
 * Facebook:
 *   Opens the classic sharer intent in a popup — this is what fixes the
 *   "wrong link preview" bug: the sharer URL is unambiguous, so Facebook
 *   pulls OG tags fresh from the given `url` instead of guessing.
 *   We also include a version query (`?v=...` on the OG image) in the
 *   sitewide meta so Facebook's cache treats our previews as fresh.
 *
 * TikTok:
 *   TikTok has no web-share intent — everything happens inside the app.
 *   We copy the link + caption to the clipboard, then open tiktok.com so
 *   the user can paste it into their bio, DM, or upload caption in one
 *   move. On mobile the browser's Web Share sheet handles it natively.
 */
export default function SocialShareButtons({
  url = "https://earthpulse.store/",
  text = "Earth Pulse — daily cosmic guidance. Space weather, Earth's heartbeat & your star alignment at birth.",
  variant = "full",         // "full" | "compact"
  testIdPrefix = "share",
}) {
  const [copied, setCopied] = useState(false);

  const shareFacebook = () => {
    const u = encodeURIComponent(url);
    const q = encodeURIComponent(text);
    const w = 600, h = 540;
    const y = window.top?.outerHeight
      ? Math.max(0, ((window.top.outerHeight - h) / 2) + (window.top.screenY || 0))
      : 100;
    const x = window.top?.outerWidth
      ? Math.max(0, ((window.top.outerWidth - w) / 2) + (window.top.screenX || 0))
      : 100;
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${q}`;
    const win = window.open(
      shareUrl,
      "fb-share",
      `toolbar=0,status=0,resizable=yes,width=${w},height=${h},top=${y},left=${x}`,
    );
    if (!win) window.location.href = shareUrl;
  };

  const shareTikTok = async () => {
    const composed = `${text}\n\n${url}`;
    // On mobile — try the OS-level share sheet so TikTok shows up as a target.
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: "Earth Pulse", text, url });
        toast.success("Shared ✨");
        return;
      } catch { /* fall through to clipboard */ }
    }
    try {
      await navigator.clipboard.writeText(composed);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast.success("Link copied — paste it into your TikTok caption or bio.", {
        description: "Opening TikTok in a new tab…",
      });
      window.open("https://www.tiktok.com/upload?lang=en", "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Couldn't copy — please copy the link manually.");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — please copy the link manually.");
    }
  };

  const isCompact = variant === "compact";
  const btnBase =
    "inline-flex items-center justify-center gap-2 rounded-full font-medium transition disabled:opacity-60";
  const size = isCompact ? "px-4 py-2 text-sm" : "px-5 py-3 text-base";

  return (
    <div
      className={`flex flex-wrap ${isCompact ? "gap-2" : "gap-3"} justify-center`}
      data-testid={`${testIdPrefix}-social-buttons`}
    >
      <button
        type="button"
        onClick={shareFacebook}
        className={`${btnBase} ${size} bg-[#1877F2] hover:bg-[#1665d0] text-white`}
        data-testid={`${testIdPrefix}-facebook-btn`}
        aria-label="Share on Facebook"
      >
        <Facebook size={isCompact ? 14 : 16} />
        Share on Facebook
      </button>
      <button
        type="button"
        onClick={shareTikTok}
        className={`${btnBase} ${size} bg-black hover:bg-neutral-900 text-white border border-white/10`}
        data-testid={`${testIdPrefix}-tiktok-btn`}
        aria-label="Share to TikTok"
      >
        <Music2 size={isCompact ? 14 : 16} />
        Share to TikTok
      </button>
      <button
        type="button"
        onClick={copyLink}
        className={`${btnBase} ${size} border border-[color:var(--gold)]/50 text-[color:var(--parchment)] hover:bg-[color:var(--gold)]/10`}
        data-testid={`${testIdPrefix}-copy-link-btn`}
        aria-label="Copy link"
      >
        {copied ? <Check size={isCompact ? 14 : 16} /> : <Copy size={isCompact ? 14 : 16} />}
        {copied ? "Link copied" : "Copy link"}
      </button>
    </div>
  );
}
